import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChildren, ElementRef, QueryList, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { HostConfigService } from '@core/services/host-config.service';
import { ServicioPreferenciaColorRol } from '@core/services/servicio-preferencia-color-rol.service';
import { ContextoService } from '@core/services/contexto.service';
import { SelectorColorComponent } from '@shared/componentes/selector-color/selector-color.component';
import { FormatoFechaPipe } from '@shared/pipes/formato-fecha.pipe';
import { FORMATOS_FECHA } from '@shared/constants/formatos';
import { CampanaNotificacionesComponent } from '@core/componentes/campana-notificaciones/campana-notificaciones.component';

@Component({
  selector: 'app-navbar',
  imports: [
    CommonModule,
    SelectorColorComponent,
    CampanaNotificacionesComponent,
    FormatoFechaPipe
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChildren('menuItem') private readonly menuItems!: QueryList<ElementRef<HTMLElement>>;

  public usuarioEstaAutenticado = false;
  public usuarioActual: AccountInfo | null = null;
  public readonly mostrarMenu = signal(false);
  public readonly mostrarSelectorColor = signal(false);
  public readonly coloresOcupados = signal(new Map<string, string>());
  public readonly colorActualRol = signal('');
  public readonly rolActualNombre = computed(() => {
    const roles = this.contextoService.obtenerRolesActuales();
    return roles[0] ?? '';
  });

  public imagenLogoFacto = 'assets/img/facto-logo.png';
  public imagenBandera = '';
  public altBandera = '';
  public textoPrincipal = '';

  public readonly nombreMostrar = computed(() => {
    this._estadoActualizado();
    const nombre = this.servicioAuth.getNombreMostrar();
    if (!nombre?.trim()) return 'Usuario';
    return nombre.trim().split(/\s+/)[0] || 'Usuario';
  });

  public readonly iniciales = computed(() => {
    this._estadoActualizado();
    const cantidadInicialesMostrar = 2;
    const nombreCompleto = this.servicioAuth.getNombreMostrar();
    if (!nombreCompleto || nombreCompleto.trim() === '' || nombreCompleto === 'Usuario') {
      return 'U';
    }
    const palabras = nombreCompleto.trim().split(/\s+/).filter((palabra: string) => palabra.length > 0);
    if (palabras.length === 0) {
      return 'U';
    } else if (palabras.length === 1) {
      const nombre = palabras[0];
      return nombre.length >= cantidadInicialesMostrar
        ? nombre.substring(0, cantidadInicialesMostrar).toUpperCase()
        : nombre.charAt(0).toUpperCase() + 'U';
    } else {
      const primerNombre = palabras[0];
      const apellido = palabras.at(-1);
      return primerNombre.charAt(0).toUpperCase() + (apellido?.charAt(0).toUpperCase() ?? 'U');
    }
  });

  public readonly email = computed(() => {
    this._estadoActualizado();
    return this.servicioAuth.getEmail();
  });

  public readonly ultimoIngreso = computed(() => {
    this._estadoActualizado();
    return this.servicioAuth.getUltimoIngreso();
  });

  public readonly FORMATOS_FECHA = FORMATOS_FECHA;

  private readonly servicioAuth = inject(AuthService);

  private readonly _estadoActualizado = signal(0);
  private readonly _destruyendo$ = new Subject<void>();
  private readonly TIEMPO_ACTUALIZACION_ESTADO_MS = 100;
  private readonly servicioTransmisionMsal = inject(MsalBroadcastService);
  private readonly detectorCambios = inject(ChangeDetectorRef);
  private readonly hostConfigService = inject(HostConfigService);
  private readonly servicioPreferenciaColorRol = inject(ServicioPreferenciaColorRol);
  private readonly contextoService = inject(ContextoService);

  public logout(): void {
    this.servicioAuth.cerrarSesion();
  }

  public toggleMenu(): void {
    this.mostrarMenu.update(v => !v);
  }

  public closeMenu(): void {
    this.mostrarMenu.set(false);
    this.mostrarSelectorColor.set(false);
  }

  public openColorSelector(): void {
    const roles = this.contextoService.obtenerRolesActuales();
    const rolActivo = roles[0] ?? '';
    const datos = this.servicioPreferenciaColorRol.obtenerDatosSelector(rolActivo);
    this.coloresOcupados.set(datos.coloresOcupados);
    this.colorActualRol.set(datos.colorActual);
    this.mostrarMenu.set(false);
    this.mostrarSelectorColor.set(true);
  }

  public onColorConfirmado(codigoColor: string): void {
    const roles = this.contextoService.obtenerRolesActuales();
    const rolActivo = roles[0] ?? '';
    this.servicioPreferenciaColorRol.guardarYAplicarColor(rolActivo, codigoColor, true)
      .pipe(takeUntil(this._destruyendo$))
      .subscribe(exito => {
        if (exito) {
          this.coloresOcupados.set(this.servicioPreferenciaColorRol.obtenerColoresOcupados());
        }
        this.mostrarSelectorColor.set(false);
        this.closeMenu();
      });
  }

  public cerrarSelectorColor(): void {
    this.mostrarSelectorColor.set(false);
  }

  public alPresionarTeclaMenu(evento: KeyboardEvent): void {
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      this.toggleMenu();
    } else if (evento.key === 'Escape') {
      evento.preventDefault();
      this.closeMenu();
    }
  }

  public alSoltarTeclaMenu(evento: KeyboardEvent): void {
    if (evento.key === ' ') {
      evento.preventDefault();
    }
  }

  public alNavegarMenu(evento: KeyboardEvent): void {
    const items = this.menuItems.toArray().map(r => r.nativeElement);
    const idx = items.indexOf(document.activeElement as HTMLElement);
    switch (evento.key) {
      case 'ArrowDown': evento.preventDefault(); items[(idx + 1) % items.length]?.focus(); break;
      case 'ArrowUp':   evento.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); break;
      case 'Escape':    evento.preventDefault(); this.closeMenu(); break;
    }
  }

  public ngOnInit(): void {
    this.initialize();
    this.configurarCompaniaPorHost();
    const contexto = this.contextoService.obtenerContextoActivo();
    if (contexto?.roles?.length) {
      this.servicioPreferenciaColorRol.cargarPreferencias().subscribe();
    }
  }

  public ngOnDestroy(): void {
    this.destroy();
  }

  private updateAuthState(): void {
    this.usuarioEstaAutenticado = this.servicioAuth.estaAutenticado();
    this.usuarioActual = this.servicioAuth.getInfoUsuario();
    this._estadoActualizado.update(v => v + 1);
  }

  private initialize(): void {
    this.updateAuthState();

    this.servicioTransmisionMsal.msalSubject$
      .pipe(
        takeUntil(this._destruyendo$)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.updateAuthState();
          this.detectorCambios.markForCheck();
        }, this.TIEMPO_ACTUALIZACION_ESTADO_MS);
      });

    this.servicioTransmisionMsal.inProgress$
      .pipe(takeUntil(this._destruyendo$))
      .subscribe(() => {
        setTimeout(() => {
          this.updateAuthState();
          this.detectorCambios.markForCheck();
        }, this.TIEMPO_ACTUALIZACION_ESTADO_MS);
      });
  }

  private destroy(): void {
    this._destruyendo$.next(undefined);
    this._destruyendo$.complete();
  }

  private configurarCompaniaPorHost(): void {
    this.textoPrincipal = this.hostConfigService.getNombreCompania();
    this.imagenBandera = this.hostConfigService.getImagenCompania();
    this.altBandera = this.textoPrincipal;
  }
}
